import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, MapPin, ArrowLeft, Plus, Weight, Package } from "lucide-react";

export default function StorePage() {
  const { sid } = useParams();
  const { add } = useCart();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/stores/${sid}`).then((r) => setStore(r.data));
    api.get(`/stores/${sid}/products`).then((r) => setProducts(r.data));
  }, [sid]);

  const addToCart = async (p) => {
    try { await add(p.id, 1); toast.success(`${p.name} added to cart`); }
    catch { toast.error("Could not add to cart"); }
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  if (!store) return <div className="h-96 rounded-2xl bg-muted animate-pulse" />;

  return (
    <div className="space-y-6">
      <Link to="/app" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to stores</Link>

      <div className="rounded-3xl overflow-hidden border bg-card">
        <div className="h-44 sm:h-56 bg-muted relative">
          {store.cover && <img src={store.cover} alt={store.name} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
          <div className="absolute bottom-4 left-5 text-white">
            <h1 className="text-2xl sm:text-3xl font-display font-black">{store.name}</h1>
            <div className="flex items-center gap-3 text-sm mt-1">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{store.rating}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{store.city}</span>
              <Badge className="bg-brand">{store.is_open ? "Open" : "Closed"}</Badge>
            </div>
          </div>
        </div>
        <div className="p-5"><p className="text-sm text-muted-foreground">{store.description}</p></div>
      </div>

      <input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products in this store…"
        className="w-full h-12 px-4 rounded-xl border bg-card text-sm" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="rounded-2xl border bg-card overflow-hidden flex flex-col hover:shadow-md transition-shadow">
            <div className="aspect-square bg-muted overflow-hidden">
              {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-3 flex flex-col flex-1">
              <h3 className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</h3>
              <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-0.5"><Weight className="w-3 h-3" />{p.weight_kg}kg</span>
                <span className="flex items-center gap-0.5"><Package className="w-3 h-3" />{p.unit}</span>
              </div>
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="font-display font-bold text-brand">{money(p.price, p.currency)}</span>
                <Button data-testid={`add-${p.id}`} size="icon" onClick={() => addToCart(p)} className="h-8 w-8 rounded-lg bg-brand hover:bg-brand-dark"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && <div className="text-center py-16 text-muted-foreground">No products found.</div>}
    </div>
  );
}
